/**
 * ProgressIndicator Component
 *
 * Purpose: Shows user progress through the 3-step calculator process
 * User Flow: Visible throughout all steps (State → Industry → Results)
 * Features: Step indicators, progress bar, navigation controls
 */

import React from 'react';
import { useCalculatorContext } from '../contexts/CalculatorContext';

const ProgressIndicator = () => {
  const { state, actions } = useCalculatorContext();

  const steps = [
    { 
      number: 1, 
      label: 'Select State',
      shortLabel: 'State',
      completed: state.currentStep > 1,
      active: state.currentStep === 1
    },
    { 
      number: 2, 
      label: 'Select Business Type',
      shortLabel: 'Business',
      completed: state.currentStep > 2,
      active: state.currentStep === 2
    },
    { 
      number: 3, 
      label: 'View Requirements',
      shortLabel: 'Results',
      completed: state.currentStep === 3 && state.calculationResults !== null,
      active: state.currentStep === 3
    }
  ];

  // Calculate progress percentage
  const progressPercentage = ((state.currentStep - 1) / (steps.length - 1)) * 100;

  // Handle step click (only allow going back)
  const handleStepClick = (stepNumber) => {
    if (stepNumber < state.currentStep) {
      // Allow navigation backwards
      // Set step directly instead of using while loop
      actions.setStep(stepNumber);
    }
  };

  return (
    <div className="fss-progress-indicator">
      {/* Progress bar */}
      <div className="fss-progress-bar-container">
        <div 
          className="fss-progress-bar-fill" 
          style={{ width: `${progressPercentage}%` }}
          role="progressbar"
          aria-valuenow={progressPercentage}
          aria-valuemin="0"
          aria-valuemax="100"
        />
      </div>

      {/* Step indicators */}
      <div className="fss-progress-steps">
        {steps.map((step, index) => (
          <React.Fragment key={step.number}>
            <div 
              className={`fss-progress-step ${step.active ? 'fss-active' : ''} ${step.completed ? 'fss-completed' : ''}`}
              onClick={() => handleStepClick(step.number)}
              role="button"
              tabIndex={step.number < state.currentStep ? 0 : -1}
              aria-label={`${step.label} - ${step.completed ? 'Completed' : step.active ? 'Current' : 'Upcoming'}`}
            >
              {/* Step number/checkmark */}
              <div className="fss-step-indicator">
                {step.completed ? (
                  <span className="fss-step-checkmark">✓</span>
                ) : (
                  <span className="fss-step-number">{step.number}</span>
                )}
              </div>

              {/* Step label */}
              <div className="fss-step-label">
                <span className="fss-step-label-full">{step.label}</span>
                <span className="fss-step-label-short">{step.shortLabel}</span>
              </div>

              {/* Step status indicator */}
              {step.active && (
                <div className="fss-step-status fss-active-status">Current</div>
              )}
              {step.completed && !step.active && (
                <div className="fss-step-status fss-completed-status">Done</div>
              )}
            </div>

            {/* Connector line between steps */}
            {index < steps.length - 1 && (
              <div className={`fss-step-connector ${step.completed ? 'fss-completed' : ''}`}>
                <div className="fss-connector-line"></div>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Selected information display */}
      {(state.selectedState || state.selectedBusinessType) && (
        <div className="fss-progress-selections">
          {state.selectedState && (
            <div className="fss-selection-item">
              <span className="fss-selection-label">State:</span>
              <span className="fss-selection-value">{state.selectedState}</span>
              {state.currentStep > 1 && (
                <button 
                  className="fss-selection-change"
                  onClick={() => handleStepClick(1)}
                  aria-label="Change state"
                >
                  Change
                </button>
              )}
            </div>
          )}
          {state.selectedBusinessType && (
            <div className="fss-selection-item">
              <span className="fss-selection-label">Business:</span>
              <span className="fss-selection-value">
                {state.selectedBusinessType.replace(/([A-Z])/g, ' $1').trim()}
              </span>
              {state.currentStep > 2 && (
                <button 
                  className="fss-selection-change"
                  onClick={() => handleStepClick(2)}
                  aria-label="Change business type"
                >
                  Change
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Navigation helpers */}
      <div className="fss-progress-navigation">
        {state.canGoBack && (
          <button 
            className="fss-nav-button fss-back-button"
            onClick={actions.goBack}
            aria-label="Go back to previous step"
          >
            <span className="fss-nav-icon">←</span>
            <span className="fss-nav-label">Back</span>
          </button>
        )}

        {state.currentStep === 3 && (
          <button 
            className="fss-nav-button fss-reset-button"
            onClick={actions.reset}
            aria-label="Start over"
          >
            <span className="fss-nav-icon">↻</span>
            <span className="fss-nav-label">Start Over</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default ProgressIndicator;
